import { Box, Button, Grid, Typography } from '@mui/material';
import { GridColDef, GridSelectionModel } from '@mui/x-data-grid-pro';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import useSWR from 'swr';

import { dataGridCol } from '@shared/components/grid/common';
import { PhoenixGrid } from '@shared/components/grid/PhoenixGrid';
import { AlertModal } from '@shared/components/modals/AlertModal';
import { Notistack } from '@shared/components/Notistack/Notistack';
import { SERVICES } from '@shared/constants/apiConfig';
import { PageWrapper } from '@shared/styledComponents';
import { StipReviewGridVertical } from '@styles/CommonStyles';
import { StyledFileNameButton } from '@styles/StyledButtons';
import { colorTheme } from '@styles/muiTheme';
import { gridDateFormatter, queryParams, withQueryParams } from '@shared/commonFunctions';
import { useTabs } from '@shared/hooks/useTabs';
import { GridCell } from '@shared/components/grid/GridCell';
import { IContactData, IDeal } from '../../Deals/interfaces';
import { ALERT_TYPES, AUTO_SELECT_STIPS_TYPES, DocumentSubmitType, IContactFile, IContactFileResponse, pushToLender } from '../interfaces';
import { requestDeleteStipFiles, requestRestoreStipFiles, requestUploadStips, requestUploadStipsToDocuSign } from '../requests';

import { StipsReview } from './StipsReview';
import { ConfidenceScore } from './FilesTable';

export const StipsReviewTab = () => {
  const params = queryParams();
  const location = useLocation();
  const { updateCurrentTab } = useTabs();

  const [selectedIds, setSelectedIds] = useState<GridSelectionModel>([]);
  const [openAlertModal, setOpenAlertModal] = useState(false);
  const [alertType, setAlertType] = useState<string>('');
  const [alertProps, setAlertProps] = useState<{ title: string; subtitle: string } | null>(null);
  const { data: contact } = useSWR<IContactData>(`${SERVICES.CONTACT}/contacts/${params.id}`);
  const { data: deal } = useSWR<IDeal>(params.dealId && `/${SERVICES.DEAL}/deals/${params.dealId}`);
  const {
    data: response,
    mutate: reloadFiles,
    isValidating,
    error,
  } = useSWR<IContactFileResponse>(
    withQueryParams(
      `${SERVICES.DOCUMENT}/stips/contacts/${params.id}`,
      { dealId: params.dealId, assetId: deal?.assetId, includeDeleted: false },
      false
    )
  );
  const [contactStipData, setContactStipData] = useState<IContactFile>();
  const [contactStipId, setContactStipId] = useState('');
  const [loading, setLoading] = useState(false);
  const [isPushEnabled, setIsPushEnabled] = useState(false);
  const submitType =deal?.lenderResponse.isQuilo ? DocumentSubmitType.Qlo : deal?.lenderResponse.documentSubmitType;
  const isQuilo = deal?.lenderResponse.isQuilo ? true : (deal?.lenderResponse.documentSubmitType == DocumentSubmitType.Qlo);
  const isLoading = useMemo(() => (!response && !error) || isValidating, [response, error, isValidating]);

  useEffect(() => {
    if (contact) {
      const title = `${contact.firstName} ${contact.lastName}-STIPS`;

      updateCurrentTab({
        route: `${location.pathname.toString()}${location.search}`,
        title,
      });
    }
  }, [location.pathname, location.search, contact]);

  useEffect(() => {
    if (response?.documents && !isLoading) {
      const initialSelectedIds = response?.documents
      .filter(document => AUTO_SELECT_STIPS_TYPES.some((stipType) => stipType === document?.stipType))
      .map(document => document?.contactStipId);
      setSelectedIds(initialSelectedIds);
    }
  }, [response?.documents, isLoading]);

  const disablePushButton = useMemo(() => {
    return (
      submitType === DocumentSubmitType.Flagship ||
      submitType === DocumentSubmitType.Meridian ||
      submitType === DocumentSubmitType.Gls ||
      submitType === DocumentSubmitType.Wtl || 
      submitType === DocumentSubmitType.Cudl ||
      submitType === DocumentSubmitType.Culs
    );
  }, [submitType]);

  const onSelection = (ids: GridSelectionModel) => {
    setIsPushEnabled(false);
    if (isQuilo && ids.length === 1) {
      const doc =  response?.documents.find(a => a.contactStipId === ids[0] && a.uploadedToDocusignDate !== null);
      if (doc && doc.stipType === 'Quilo Funding Packet') {
        setIsPushEnabled(true);
      }
    }
    setSelectedIds(ids);

  };

  const pushStipsToLender = useCallback(async () => {
    setLoading(true);
    if (submitType) {
      await requestUploadStips(pushToLender[submitType], params.dealId, selectedIds);
    }
    setLoading(false);
  }, [submitType, selectedIds, params.dealId]);

  const uploadStipsToDocuSign = async () => {
    setLoading(true);
    await requestUploadStipsToDocuSign(params.dealId, params.id, selectedIds);
    setLoading(false);
  };

  const handleClose = useCallback(() => {
    if (!loading) {
      setOpenAlertModal(false);
      setAlertType('');
    }
  }, [loading]);

  const handleConfirm = useCallback(async () => {
    switch (alertType) {
      case ALERT_TYPES.DELETE:
        await requestDeleteStipFiles(contactStipId, params.id, false);
        break;
      case ALERT_TYPES.RESTORE:
        await requestRestoreStipFiles(contactStipId, params.id);
        break;
      case ALERT_TYPES.ADMIN_DELETE:
        await requestDeleteStipFiles(contactStipId, params.id, true);
        break;
      case ALERT_TYPES.PUSH_STIPS:
        await pushStipsToLender();
        break;
      case ALERT_TYPES.UPLOAD_STIPS:
        await uploadStipsToDocuSign();
        break;
      default:
        break;
    }
    reloadFiles();
    setOpenAlertModal(false);
    setAlertType('');
  }, [alertType]);

  const handleOnClick = useCallback(
    (row: IContactFile | null, action: string) => {
      row && setContactStipId(row.contactStipId);
      let props = { title: '', subtitle: '' };
      switch (action) {
        case ALERT_TYPES.DELETE:
          props = { title: 'Delete', subtitle: 'Are you sure you want to Delete this?' };
          break;
        case ALERT_TYPES.RESTORE:
          props = { title: 'Restore', subtitle: 'Are you sure you want to Restore this?' };
          break;
        case ALERT_TYPES.ADMIN_DELETE:
          props = {
            title: 'Delete Permanently',
            subtitle: 'Are you sure you want to delete this document PERMANENTLY ?',
          };
          break;
        case ALERT_TYPES.PUSH_STIPS:
          props = { title: 'Push Stips to Lender', subtitle: 'Do you want to push these STIPS to Lender?' };
          break;
        case ALERT_TYPES.UPLOAD_STIPS:
          props = { title: 'Upload STIPS to DocuSign', subtitle: 'Do you want to upload these STIPS to DocuSign?' };
          break;
        default:
          break;
      }
      setAlertProps(props);
      setAlertType(action);
      setOpenAlertModal(true);
    },
    [alertType]
  );

  const createGridColumn = (field: string, headerText: string, flex = 0.7, minWidth = 94) => {
    return dataGridCol(field, {
      renderHeader: () => <GridCell isWordBreak isBold line={2} text={headerText} />,
      flex,
      minWidth,
      valueFormatter: gridDateFormatter,
      textWrap: true
    });
  };

  const handleStipReview = useCallback((stipId: string) => {
    const route = withQueryParams('/stips-review', { id: params.id, dealId: params.dealId, assetId: params.assetId, stipId: stipId}, false);
    window.open(route, '_blank');
  }, [contact]);

  const columns: GridColDef[] = [
    dataGridCol('confidenceScore', {
          headerName: 'Confidence Score',
          minWidth: 110,
          sortable: true,
          align: 'center',
          headerAlign: 'center',
          renderCell: ({ row }) => <ConfidenceScore score={row.ocrResult?.confidenceScore} onClick={() => handleStipReview(row.contactStipId)}/>,
        }),
    createGridColumn('uploadedToDocusignDate', 'DocuSign Upload Date'),
    createGridColumn('pushedToLenderDate', 'Lender Upload Date'),
    createGridColumn('uploadedOn', 'Uploaded On'),
    dataGridCol('stipType', {
      flex: 1,
      minWidth: 120,
      textWrap: true
    }),
    dataGridCol('fileName', {
      minWidth: 120,
      flex: 1,
      renderCell: ({ row, value }) => (
        <StyledFileNameButton
          fullWidth
          variant="text"
          onClick={() => setContactStipData(row)}
        >
          {value}
        </StyledFileNameButton>
      ),
    }),
  ];

  return (
    <PageWrapper>
      <Grid
        container
        spacing={2}
        pb={2}
        sx={{ borderBottom: 0.5 }}
      >
        <Grid
          item
          md={5}
        >
          <Typography
            variant="h3"
            color={colorTheme.palette.primary.main}
          >
            STIPS Review - {contact?.firstName} {contact?.lastName}
          </Typography>
          <Box
            mb={1}
            display="flex"
            justifyContent="space-between"
          >
            {openAlertModal && alertProps && (
              <AlertModal
                title={alertProps?.title}
                subtitle={alertProps?.subtitle}
                buttonText={alertType === 'pushStips' || alertType === 'uploadStips' ? 'OK' : 'Confirm'}
                open={openAlertModal}
                isLoading={loading}
                onConfirm={handleConfirm}
                onClose={handleClose}
              />
            )}
            <Button
              variant="contained"
              type="submit"
              size={'small'}
              disabled={!(disablePushButton || isPushEnabled)}
              onClick={() =>
                !selectedIds.length
                  ? Notistack.generalError('No Documents Selected')
                  : handleOnClick(null, ALERT_TYPES.PUSH_STIPS)
              }
            >
              Push STIPS to Lender
            </Button>
            <Button
              variant="contained"
              type="submit"
              size={'small'}
              onClick={() =>
                !selectedIds.length
                  ? Notistack.generalError('No Documents Selected')
                  : handleOnClick(null, ALERT_TYPES.UPLOAD_STIPS)
              }
            >
              Upload to Docu Sign
            </Button>
          </Box>
          <PhoenixGrid
            withHeader
            canNotCopy
            canNotEdit
            noButton
            checkboxSelection
            isFlexible
            autoRowHeight
            disableSelectionOnClick
            disablePointer
            height="650px"
            pageSize={10}
            columns={columns}
            selectionModel={selectedIds}
            loading={isLoading}
            getRowId={(row) => row.contactStipId}
            rows={isLoading ? [] : response?.documents ?? []}
            onSelectionModelChange={onSelection}
          />
        </Grid>
        {contactStipData && contactStipData.contactStipId ? (
          <Grid
            item
            md={7}
          >
            <StipsReview
              key={contactStipData.contactStipId}
              contactStipData={contactStipData as IContactFile}
            />
          </Grid>
        ) : (
          <StipReviewGridVertical
            item
            md={7}
          >
            <Typography>Please select a file</Typography>
          </StipReviewGridVertical>
        )}
      </Grid>
    </PageWrapper>
  );
};
